import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { API } from '../App';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { motion } from 'framer-motion';
import { FileText, Clock, Target, TrendingUp, AlertCircle, CheckCircle2, Upload, BarChart3, Users, BookOpen, Star, MessageCircle } from 'lucide-react';
import { toast } from 'sonner';

const MISTAKE_HINTS = {
  'Silly Mistake (rushed)': 'Answered far too quickly — re-read the question and verify the option before locking it in.',
  'Conceptual Gap': 'The underlying theory is not solid yet — revise the concept before attempting more questions.',
  'Application Error': 'Concept is known but the method slips — write every step instead of solving mentally.',
  'Time Pressure': 'Spent too long before answering — cap the attempt and move on rather than over-investing.',
  'Skipped / Not Attempted': 'Left blank — attempt everything you have revised; blanks cost no negative marks but no marks either.',
  Conceptual: 'Theory gaps — revise the concept and redo the same questions.',
  Calculation: 'Arithmetic and unit slips — recheck each step and units.',
  Misreading: 'Question was misread — underline what is being asked before solving.',
  Casual: 'Careless attempt — slow down on the final step.',
  'Time Management': 'Pacing issue — practise timed sets to build rhythm.',
};


const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [tests, setTests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [topTeachers, setTopTeachers] = useState([]);
  const [settings, setSettings] = useState(null);
  const [supportContact, setSupportContact] = useState(null);
  const [overallAnalysis, setOverallAnalysis] = useState(null);
  const [overallAnalyzing, setOverallAnalyzing] = useState(false);

  useEffect(() => {
    fetchDashboardData();
    fetchOverallAnalysis();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [statsRes, testsRes, settingsRes, contactRes] = await Promise.all([
        axios.get(`${API}/dashboard/stats`),
        axios.get(`${API}/tests`),
        axios.get(`${API}/settings`),
        axios.get(`${API}/dashboard/support-contact`)
      ]);
      setStats(statsRes.data);
      setTests(testsRes.data.sort((a, b) => new Date(b.created_at) - new Date(a.created_at)));
      setSettings(settingsRes.data);
      setSupportContact(contactRes.data);
      
      // Fetch top 3 teachers
      const teachersRes = await axios.get(`${API}/teachers`);
      setTopTeachers(teachersRes.data.sort((a, b) => b.rating - a.rating).slice(0, 3));
    } catch (error) {
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const fetchOverallAnalysis = async () => {
    try {
      const response = await axios.get(`${API}/overall-analysis`);
      setOverallAnalysis(response.data);
    } catch (error) {
      setOverallAnalysis(null);
    }
  };

  const generateOverallAnalysis = async () => {
    setOverallAnalyzing(true);
    try {
      const response = await axios.post(`${API}/overall-analysis`);
      setOverallAnalysis(response.data);
      toast.success('Overall analysis generated successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate overall analysis');
    } finally {
      setOverallAnalyzing(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-lg text-slate-600">Loading your dashboard...</div>
      </div>
    );
  }

  const StatCard = ({ icon: Icon, label, value, color, subtitle }) => (
    <motion.div
      whileHover={{ scale: 1.02, y: -5 }}
      transition={{ type: 'spring', stiffness: 300 }}
    >
      <Card className="border-0 shadow-lg hover:shadow-xl transition-all duration-300">
        <CardContent className="p-6">
          <div className="flex items-start justify-between">
            <div className="space-y-2">
              <p className="text-sm font-medium text-slate-600">{label}</p>
              <p className={`text-3xl font-bold ${color}`}>{value}</p>
              {subtitle && <p className="text-xs text-slate-500">{subtitle}</p>}
            </div>
            <div className={`w-12 h-12 rounded-xl ${color.replace('text', 'bg').replace('600', '100')} flex items-center justify-center`}>
              <Icon className={`w-6 h-6 ${color}`} />
            </div>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-testid="dashboard-page">
      <div className="mb-8">
        <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-2">Dashboard</h1>
        <p className="text-lg text-slate-600">Track your performance and progress</p>
      </div>

      {/* Hero Action Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Practice Test CTA */}
        <Card className="border-0 shadow-xl bg-gradient-to-br from-violet-50 to-indigo-50" data-testid="practice-test-cta">
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <div className="w-14 h-14 rounded-2xl bg-violet-600 flex items-center justify-center flex-shrink-0">
                <FileText className="w-7 h-7 text-white" />
              </div>
              <div className="flex-1">
                <h2 className="text-xl font-bold text-slate-900 mb-1">Start a Practice Test</h2>
                <p className="text-slate-600 text-sm mb-4">Generate custom tests by topic or full-length papers</p>
                <Button asChild className="bg-violet-600 hover:bg-violet-700 text-white rounded-full" data-testid="dashboard-start-practice">
                  <Link to="/practice">Create Practice Test</Link>
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Book Teacher Session CTA - NEW PROMINENT CARD */}
        <Card className="border-0 shadow-xl bg-gradient-to-br from-amber-50 to-orange-50 relative overflow-hidden" data-testid="book-teacher-cta">
          <div className="absolute top-0 right-0 w-32 h-32 bg-amber-200/30 rounded-full -translate-y-1/2 translate-x-1/2" />
          <CardContent className="p-6 relative">
            <div className="flex items-start gap-4">
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center flex-shrink-0 shadow-lg shadow-amber-500/30">
                <Users className="w-7 h-7 text-white" />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <h2 className="text-xl font-bold text-slate-900">Book Session</h2>
                  <span className="px-2 py-0.5 bg-amber-500 text-white text-xs font-semibold rounded-full">NEW</span>
                </div>
                <p className="text-slate-600 text-sm mb-4">Get personalized help with topic learning or doubt solving from expert teachers</p>
                <Button asChild className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white rounded-full shadow-md px-6" data-testid="dashboard-book-teacher">
                  <Link to="/teachers">
                    <Users className="w-4 h-4 mr-2" />
                    Find & Book a Teacher
                  </Link>
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>


      {stats && stats.total_tests > 0 ? (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <StatCard
              icon={FileText}
              label="Tests Taken"
              value={stats.total_tests}
              color="text-violet-600"
            />
            <StatCard
              icon={Target}
              label="Overall Accuracy"
              value={`${stats.accuracy}%`}
              color="text-emerald-600"
              subtitle={`${stats.total_questions} questions`}
            />
            <StatCard
              icon={Clock}
              label="Total Study Time"
              value={`${stats.total_time_minutes}m`}
              color="text-amber-600"
            />
            <StatCard
              icon={TrendingUp}
              label="Questions Attempted"
              value={stats.total_questions}
              color="text-indigo-600"
            />
          </div>

          {stats.mistake_distribution && Object.keys(stats.mistake_distribution).length > 0 && (
            <Card className="mb-8 border-0 shadow-lg" data-testid="mistake-distribution-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <AlertCircle className="w-5 h-5 text-amber-600" />
                  Mistake Distribution
                </CardTitle>
                <CardDescription>Understanding your error patterns</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {Object.entries(stats.mistake_distribution)
                    .sort((a, b) => b[1] - a[1])
                    .map(([type, count]) => {
                      const total = Object.values(stats.mistake_distribution).reduce((a, b) => a + b, 0);
                      const percentage = ((count / total) * 100).toFixed(1);
                      return (
                        <div key={type} className="space-y-1">
                          <div className="flex justify-between text-sm">
                            <span className="font-medium text-slate-700">{type}</span>
                            <span className="text-slate-500">{count} ({percentage}%)</span>
                          </div>
                          <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                            <motion.div
                              initial={{ width: 0 }}
                              animate={{ width: `${percentage}%` }}
                              transition={{ duration: 0.5, delay: 0.1 }}
                              className="h-full bg-gradient-to-r from-amber-500 to-orange-500"
                            />
                          </div>
                        </div>
                      );
                    })}
                </div>
              </CardContent>
            </Card>
          )}

          {!overallAnalysis ? (
            <Card className="mb-8 border-0 shadow-xl bg-gradient-to-br from-emerald-50 to-teal-50" data-testid="dashboard-generate-overall-analysis-card">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 rounded-full bg-emerald-600 flex items-center justify-center mx-auto mb-4">
                  <TrendingUp className="w-8 h-8 text-white" />
                </div>
                <h2 className="text-2xl font-bold text-slate-900 mb-2">Generate Overall Progress Analysis</h2>
                <p className="text-slate-600 mb-6 max-w-2xl mx-auto leading-relaxed">
                  Get a holistic view across all your tests: subject mastery, chapter gaps, time patterns, and mistake trends.
                </p>
                <Button
                  onClick={generateOverallAnalysis}
                  disabled={overallAnalyzing}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-full px-8 h-12 font-medium shadow-lg shadow-emerald-500/25"
                  data-testid="dashboard-generate-overall-analysis-button"
                >
                  {overallAnalyzing ? 'Analyzing Overall Performance...' : 'Generate Overall Analysis'}
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-6 mb-8" data-testid="dashboard-overall-analysis-section">
              <Card className="border-0 shadow-xl bg-gradient-to-br from-emerald-50 to-teal-50">
                <CardHeader>
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <CardTitle className="flex items-center gap-2">
                        <TrendingUp className="w-6 h-6 text-emerald-600" />
                        Overall Performance (All Tests)
                      </CardTitle>
                      <CardDescription>
                        Consolidated insights across offline uploads and practice tests
                        {overallAnalysis.created_at && (
                          <> · updated {new Date(overallAnalysis.created_at).toLocaleString()}</>
                        )}
                      </CardDescription>
                    </div>
                    <Button
                      variant="outline"
                      onClick={generateOverallAnalysis}
                      disabled={overallAnalyzing}
                      className="rounded-full border-emerald-300 text-emerald-700 hover:bg-emerald-100"
                      data-testid="dashboard-refresh-overall-analysis-button"
                    >
                      {overallAnalyzing ? 'Refreshing...' : 'Refresh analysis'}
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-lg text-slate-700 leading-relaxed" data-testid="dashboard-overall-analysis-insights">
                    {overallAnalysis.overall_insights}
                  </p>
                </CardContent>
              </Card>

              <Card className="border-0 shadow-lg">
                <CardHeader>
                  <CardTitle>Overall Breakdown</CardTitle>
                  <CardDescription>Subject, chapter, topic, time and mistake patterns</CardDescription>
                </CardHeader>
                <CardContent>
                  <Tabs defaultValue="subjects">
                    <TabsList className="mb-4">
                      <TabsTrigger value="subjects" data-testid="dashboard-overall-subjects-tab">Subjects</TabsTrigger>
                      <TabsTrigger value="chapters" data-testid="dashboard-overall-chapters-tab">Chapters</TabsTrigger>
                      <TabsTrigger value="topics" data-testid="dashboard-overall-topics-tab">Topics</TabsTrigger>
                      <TabsTrigger value="mistakes" data-testid="dashboard-overall-mistakes-tab">Mistakes</TabsTrigger>
                      <TabsTrigger value="time" data-testid="dashboard-overall-time-tab">Time</TabsTrigger>
                    </TabsList>

                    <TabsContent value="subjects">
                      <div className="space-y-4">
                        {Object.entries(overallAnalysis.subject_analysis || {}).map(([subject, insight]) => (
                          <div key={subject} className="p-4 rounded-xl bg-slate-50 border border-slate-200">
                            <h4 className="font-semibold text-slate-900 mb-2">{subject}</h4>
                            <p className="text-slate-700" data-testid={`dashboard-overall-subject-${subject}`}>{insight}</p>
                          </div>
                        ))}
                        {Object.keys(overallAnalysis.subject_analysis || {}).length === 0 && (
                          <p className="text-sm text-slate-500">No subject insights available yet.</p>
                        )}
                      </div>
                    </TabsContent>

                    <TabsContent value="chapters">
                      <div className="space-y-4">
                        {Object.entries(overallAnalysis.chapter_analysis || {}).map(([chapter, insight]) => (
                          <div key={chapter} className="p-4 rounded-xl bg-slate-50 border border-slate-200">
                            <h4 className="font-semibold text-slate-900 mb-2">{chapter}</h4>
                            <p className="text-slate-700" data-testid={`dashboard-overall-chapter-${chapter}`}>{insight}</p>
                          </div>
                        ))}
                        {Object.keys(overallAnalysis.chapter_analysis || {}).length === 0 && (
                          <p className="text-sm text-slate-500">No chapter insights available yet.</p>
                        )}
                      </div>
                    </TabsContent>

                    <TabsContent value="topics">
                      <div className="space-y-4">
                        {Object.entries(overallAnalysis.topic_analysis || {}).map(([topic, insight]) => (
                          <div key={topic} className="p-4 rounded-xl bg-slate-50 border border-slate-200">
                            <h4 className="font-semibold text-slate-900 mb-2">{topic}</h4>
                            <p className="text-slate-700" data-testid={`dashboard-overall-topic-${topic}`}>{insight}</p>
                          </div>
                        ))}
                        {Object.keys(overallAnalysis.topic_analysis || {}).length === 0 && (
                          <p className="text-sm text-slate-500">No topic insights available yet.</p>
                        )}
                      </div>
                    </TabsContent>

                    <TabsContent value="mistakes">
                      <div className="space-y-3">
                        {Object.entries(overallAnalysis.mistake_patterns || {})
                          .sort((a, b) => Number(b[1]) - Number(a[1]))
                          .map(([mistake, count]) => {
                            const total = Object.values(overallAnalysis.mistake_patterns).reduce(
                              (sum, v) => sum + Number(v || 0), 0
                            );
                            const share = total ? Math.round((Number(count) / total) * 1000) / 10 : 0;
                            return (
                              <div key={mistake} className="p-4 rounded-xl bg-amber-50 border border-amber-200">
                                <div className="flex items-center justify-between gap-3">
                                  <h4 className="font-semibold text-amber-800">{mistake}</h4>
                                  <span className="text-sm font-semibold text-amber-700">
                                    {count} errors · {share}%
                                  </span>
                                </div>
                                <div className="h-2 mt-2 rounded-full bg-amber-100 overflow-hidden">
                                  <div className="h-full bg-amber-500" style={{ width: `${share}%` }} />
                                </div>
                                <p className="text-sm text-amber-800 mt-2" data-testid={`dashboard-overall-mistake-${mistake}`}>
                                  {MISTAKE_HINTS[mistake] || 'Review these questions and note what went wrong each time.'}
                                </p>
                              </div>
                            );
                          })}
                        {Object.keys(overallAnalysis.mistake_patterns || {}).length === 0 && (
                          <p className="text-sm text-slate-500">No mistake pattern insights available yet.</p>
                        )}
                      </div>
                    </TabsContent>

                    <TabsContent value="time">
                      <div className="space-y-3">
                        {Object.entries(overallAnalysis.time_efficiency || {}).map(([key, insight]) => (
                          <div key={key} className="p-4 rounded-xl bg-violet-50 border border-violet-200">
                            <h4 className="font-semibold text-violet-800 mb-2">{key}</h4>
                            <p className="text-violet-800" data-testid={`dashboard-overall-time-${key}`}>{insight}</p>
                          </div>
                        ))}
                        {Object.keys(overallAnalysis.time_efficiency || {}).length === 0 && (
                          <p className="text-sm text-slate-500">No time efficiency insights available yet.</p>
                        )}
                      </div>
                    </TabsContent>
                  </Tabs>
                </CardContent>
              </Card>

              {(overallAnalysis.student_improvement_score !== null && overallAnalysis.student_improvement_score !== undefined) && (
                <Card className="border-0 shadow-lg bg-gradient-to-br from-emerald-50 to-teal-50">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <TrendingUp className="w-6 h-6 text-emerald-600" />
                      Student Improvement Score (SIS)
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    <div className="text-3xl font-bold text-slate-900" data-testid="dashboard-sis-score">
                      {overallAnalysis.student_improvement_score}/100
                    </div>
                    {overallAnalysis.biggest_learning_gap && (
                      <p className="text-slate-700"><strong>Biggest gap:</strong> {overallAnalysis.biggest_learning_gap}</p>
                    )}
                    {overallAnalysis.error_personality && (
                      <p className="text-slate-700"><strong>Error personality:</strong> {overallAnalysis.error_personality}</p>
                    )}
                  </CardContent>
                </Card>
              )}

              {overallAnalysis.action_plan?.length > 0 && (
                <Card className="border-0 shadow-lg">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <CheckCircle2 className="w-6 h-6 text-emerald-600" />
                      Overall Action Plan
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {overallAnalysis.action_plan.map((step, index) => (
                        <div key={`action-${index}-${step.slice(0, 20)}`} className="p-4 rounded-xl bg-emerald-50 border border-emerald-200" data-testid={`dashboard-action-plan-item-${index}`}>
                          <p className="text-slate-700">{step}</p>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {overallAnalysis.recommendations && overallAnalysis.recommendations.length > 0 && (
                <Card className="border-0 shadow-lg">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <CheckCircle2 className="w-6 h-6 text-emerald-600" />
                      Implementable Suggestions
                    </CardTitle>
                    <CardDescription>Action steps based on all your tests</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {overallAnalysis.recommendations.map((rec, index) => (
                        <motion.div
                          key={`rec-${index}-${rec.slice(0, 20)}`}
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: index * 0.05 }}
                          className="flex items-start gap-3 p-4 rounded-xl bg-emerald-50 border border-emerald-200"
                          data-testid={`dashboard-recommendation-item-${index}`}
                        >
                          <CheckCircle2 className="w-5 h-5 text-emerald-600 mt-0.5 flex-shrink-0" />
                          <p className="text-slate-700 leading-relaxed">{rec}</p>
                        </motion.div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          )}

          {/* Quick Book a Teacher */}
          {topTeachers.length > 0 && (
            <Card className="mb-8 border-0 shadow-xl bg-gradient-to-br from-violet-50 to-indigo-50">
              <CardHeader>
                <div className="flex justify-between items-center">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <Users className="w-6 h-6 text-violet-600" />
                      Need Help? Book a Teacher
                    </CardTitle>
                    <CardDescription>
                      Personalised teaching for any subject — solo or with friends
                    </CardDescription>
                  </div>
                  <Link to="/teachers">
                    <Button variant="outline" className="rounded-full">
                      View All
                    </Button>
                  </Link>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {topTeachers.map((teacher) => (
                    <Card key={teacher.id} className="border-violet-200 hover:shadow-lg transition-all">
                      <CardContent className="p-4 space-y-3">
                        <div className="flex items-center gap-3">
                          <div className="w-12 h-12 rounded-full bg-gradient-to-br from-violet-100 to-indigo-100 flex items-center justify-center">
                            <BookOpen className="w-6 h-6 text-violet-600" />
                          </div>
                          <div className="flex-1">
                            <h4 className="font-semibold text-slate-900">{teacher.name}</h4>
                            <div className="flex items-center gap-1">
                              <Star className="w-3 h-3 fill-amber-400 text-amber-400" />
                              <span className="text-xs font-medium">{teacher.rating.toFixed(1)}</span>
                              <span className="text-xs text-slate-500">({teacher.total_ratings})</span>
                            </div>
                          </div>
                        </div>
                        
                        <div className="flex flex-wrap gap-1">
                          {teacher.subjects.slice(0, 3).map(subject => (
                            <span key={subject} className="text-xs px-2 py-1 bg-violet-100 text-violet-700 rounded-full">
                              {subject}
                            </span>
                          ))}
                        </div>

                        <div className="text-sm">
                          <span className="text-slate-600">{teacher.experience_years} years exp</span>
                          <span className="mx-2">•</span>
                          <span className="font-semibold text-violet-600">₹{teacher.rate_per_hour}/hr</span>
                        </div>

                        <Link to={`/teacher/${teacher.id}`}>
                          <Button size="sm" className="w-full bg-violet-600 hover:bg-violet-700 rounded-full">
                            Book Session
                          </Button>
                        </Link>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}


          <Card className="border-0 shadow-lg">
            <CardHeader>
              <div className="flex justify-between items-center">
                <div>
                  <CardTitle>Recent Tests</CardTitle>
                  <CardDescription>Your latest test performances</CardDescription>
                </div>
                <Link to="/upload">
                  <Button className="bg-violet-600 hover:bg-violet-700 rounded-full" data-testid="upload-new-test-button">
                    <Upload className="w-4 h-4 mr-2" />
                    Upload New Test
                  </Button>
                </Link>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3" data-testid="recent-tests-list">
                {tests.slice(0, 10).map((test, index) => (
                  <motion.div
                    key={test.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                  >
                    <Link to={`/test/${test.id}`} data-testid={`test-link-${test.id}`}>
                      <div className="flex items-center justify-between p-4 rounded-xl border border-slate-200 hover:border-violet-300 hover:shadow-md transition-all duration-300 group">
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-violet-100 to-indigo-100 flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                            <FileText className="w-6 h-6 text-violet-600" />
                          </div>
                          <div>
                            <h3 className="font-semibold text-slate-900 group-hover:text-violet-600 transition-colors">
                              {test.test_name}
                            </h3>
                            <p className="text-sm text-slate-500">
                              {new Date(test.test_date).toLocaleDateString()} • {test.total_questions} questions
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <Button
                            variant="outline"
                            className="rounded-full group-hover:bg-violet-50 group-hover:border-violet-300"
                            data-testid={`view-analysis-button-${test.id}`}
                          >
                            <BarChart3 className="w-4 h-4 mr-2" />
                            View Analysis
                          </Button>
                        </div>
                      </div>
                    </Link>
                  </motion.div>
                ))}
              </div>
            </CardContent>
          </Card>
        </>
      ) : (
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.3 }}
          className="space-y-6"
        >
          <Card className="border-0 shadow-2xl shadow-violet-500/10" data-testid="empty-state-card">
            <CardContent className="flex flex-col items-center justify-center py-16 px-6 text-center">
              <div className="w-24 h-24 rounded-full bg-gradient-to-br from-violet-100 to-indigo-100 flex items-center justify-center mb-6">
                <TrendingUp className="w-12 h-12 text-violet-600" />
              </div>
              <h2 className="text-2xl font-bold text-slate-900 mb-2">Start Your Learning Journey</h2>
              <p className="text-slate-600 mb-8 max-w-md leading-relaxed">
                Upload your first test to get personalized insights and strategic recommendations for improvement.
              </p>
              <Link to="/upload">
                <Button
                  size="lg"
                  className="bg-violet-600 hover:bg-violet-700 text-white rounded-full px-8 h-12 font-medium shadow-lg shadow-violet-500/25"
                  data-testid="get-started-button"
                >
                  <Upload className="w-5 h-5 mr-2" />
                  Upload Your First Test
                </Button>
              </Link>
            </CardContent>
          </Card>

          {/* Book Teacher CTA for new users */}
          <Card className="border-0 shadow-xl bg-gradient-to-br from-amber-50 to-orange-50" data-testid="empty-state-book-teacher">
            <CardContent className="p-8 text-center">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center mx-auto mb-4 shadow-lg shadow-amber-500/30">
                <Users className="w-8 h-8 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-slate-900 mb-2">Need Help Getting Started?</h2>
              <p className="text-slate-600 mb-6 max-w-lg mx-auto">
                Book a session with an expert teacher for topic learning, doubt solving, or exam preparation guidance.
              </p>
              <Button asChild className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white rounded-full px-8 h-12 shadow-md" data-testid="empty-book-teacher-btn">
                <Link to="/teachers">
                  <Users className="w-5 h-5 mr-2" />
                  Book a Teacher Session
                </Link>
              </Button>
            </CardContent>
          </Card>
        </motion.div>
      )}

      <Card className="mt-8 border-0 shadow-xl bg-gradient-to-br from-emerald-50 to-teal-50" data-testid="executive-support-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MessageCircle className="w-6 h-6 text-emerald-600" />
            {supportContact?.source === 'admin' ? 'Talk to our Admin Team' : 'Talk to your Executive'}
          </CardTitle>
          <CardDescription>
            {supportContact?.source === 'admin'
              ? 'You will be assigned a dedicated Executive shortly. Until then our Admin team is here to help.'
              : 'Need help with bookings or payments? Reach out instantly.'}
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="text-sm text-slate-700 space-y-1" data-testid="dashboard-executive-details">
            <p className="font-semibold" data-testid="dashboard-executive-name">
              {supportContact?.name || 'Contact not set'}
            </p>
            <p className="text-xs text-emerald-700" data-testid="dashboard-executive-role">
              {supportContact?.designation || supportContact?.role_label}
            </p>
            <p data-testid="dashboard-executive-whatsapp">
              {supportContact?.whatsapp || 'WhatsApp number not set'}
            </p>
            {supportContact?.availability_slots?.length > 0 && (
              <div data-testid="dashboard-executive-availability">
                <p className="text-xs text-slate-500">Availability</p>
                <ul className="list-disc list-inside text-xs text-slate-500">
                  {supportContact.availability_slots.map((slot, index) => (
                    <li key={`${slot}-${index}`} data-testid={`dashboard-executive-slot-${index}`}>
                      {slot}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
          {supportContact?.whatsapp ? (
            <Button
              asChild
              className="bg-emerald-600 hover:bg-emerald-700 rounded-full"
              data-testid="dashboard-whatsapp-button"
            >
              <a
                href={`https://wa.me/${supportContact.whatsapp.replace(/[^0-9]/g, '')}?text=${encodeURIComponent(
                  `Hi ${supportContact.name || 'there'}, I need help with my WISE account.`
                )}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                Chat on WhatsApp
              </a>
            </Button>
          ) : (
            <Button variant="outline" className="rounded-full" disabled data-testid="dashboard-whatsapp-disabled">
              WhatsApp contact not available
            </Button>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Dashboard;